import { X } from 'lucide-react';
import { memo, useCallback, useRef, useState } from 'react';

import DevicesList from '../components/devices/DevicesList';
import MapView from '../components/MapView';
import { Button } from '../components/ui';
import { useIntl } from '../hooks';

const MAP_HEIGHT_DEFAULT = 480;
const MAP_HEIGHT_MIN = 200;
const MAP_HEIGHT_MAX = 800;

export default memo(function Devices() {
  const intl = useIntl();
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [mapHeight, setMapHeight] = useState(MAP_HEIGHT_DEFAULT);
  const mapHeightRef = useRef(MAP_HEIGHT_DEFAULT);
  const dragRef = useRef<{ startY: number; startHeight: number } | null>(null);
  const rafRef = useRef<number | null>(null);

  const handleSelectDevice = useCallback((deviceId: string) => {
    setSelectedDeviceId((prev) => (prev === deviceId ? null : deviceId));
  }, []);

  const handleShowAllDevices = useCallback(() => {
    setSelectedDeviceId('show-all');
  }, []);

  const handleCloseMap = useCallback(() => {
    setSelectedDeviceId(null);
  }, []);

  // Stable callback — reads mapHeightRef instead of closing over mapHeight state,
  // so it is never recreated on drag ticks.
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startY: e.clientY, startHeight: mapHeightRef.current };

    const handleMouseMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const newHeight = Math.max(
        MAP_HEIGHT_MIN,
        Math.min(MAP_HEIGHT_MAX, dragRef.current.startHeight + e.clientY - dragRef.current.startY),
      );
      // Throttle state updates to one per animation frame to avoid excessive re-renders.
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        mapHeightRef.current = newHeight;
        setMapHeight(newHeight);
      });
    };

    const handleMouseUp = () => {
      dragRef.current = null;
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const isShowAll = selectedDeviceId === 'show-all';

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="bg-background-light rounded-lg shadow-sm border border-border">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-lg font-semibold">{intl.formatMessage({ id: 'devices.map' })}</h2>
          {selectedDeviceId && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCloseMap}
              title={intl.formatMessage({ id: 'devices.clearSelection' })}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">
                {intl.formatMessage({ id: 'devices.clearSelection' })}
              </span>
            </Button>
          )}
        </div>
        <div style={{ height: mapHeight }}>
          <MapView
            isActive={true}
            selectedDeviceId={isShowAll ? null : selectedDeviceId}
            showAllDevices={isShowAll}
            onSelectedPopupClose={handleCloseMap}
          />
        </div>
        <button
          type="button"
          aria-label={intl.formatMessage({ id: 'devices.resizeMap' })}
          className="flex w-full items-center justify-center h-3 cursor-row-resize bg-border/30 hover:bg-border/60 transition-colors"
          onMouseDown={handleDragStart}
        >
          <div className="flex gap-0.5">
            <span className="w-4 h-0.5 rounded-full bg-muted-foreground/40" />
            <span className="w-4 h-0.5 rounded-full bg-muted-foreground/40" />
            <span className="w-4 h-0.5 rounded-full bg-muted-foreground/40" />
          </div>
        </button>
      </div>

      <DevicesList
        onSelectDevice={handleSelectDevice}
        selectedDeviceId={isShowAll ? null : selectedDeviceId}
        onShowAllDevices={handleShowAllDevices}
      />
    </div>
  );
});
